import { queryOptions } from "@tanstack/react-query";
import {
  getConversations,
  type GetConversationsProps,
  type GetConversationsResponse,
} from "api/conversations";
import { queryClient } from "api/query-client";

export const conversationsQueryKeys = {
  all: ["inbox", "conversations"] as const,
  list: (params: GetConversationsProps) =>
    [...conversationsQueryKeys.all, params] as const,
};

export function conversationsQueryOptions(params: GetConversationsProps) {
  // Normalize API defaults in the key so `limit: undefined` and `limit: 50`
  // share the same request and cached response.
  const normalizedParams = {
    ...params,
    limit: params.limit ?? 50,
  };

  // Align the cache key with the actual request params (the API layer drops nulls).
  const normalizedKeyParams = {
    ...normalizedParams,
    next: normalizedParams.next ?? undefined,
    assignedTo: normalizedParams.assignedTo ?? undefined,
    unassigned: normalizedParams.unassigned ?? undefined,
    ignoredAccountsComments:
      normalizedParams.ignoredAccountsComments ?? undefined,
  };

  return queryOptions({
    queryKey: conversationsQueryKeys.list(
      normalizedKeyParams as unknown as GetConversationsProps
    ),
    queryFn: (): Promise<GetConversationsResponse> =>
      getConversations(normalizedParams),
    // Dashboard setup can request the same first page through several Redux
    // initialization paths. Keep it fresh briefly to coalesce both concurrent
    // and immediately sequential dispatches without delaying normal refreshes.
    staleTime: 5000,
    retry: false,
  });
}

export function fetchConversationsQuery(params: GetConversationsProps) {
  return queryClient.fetchQuery(conversationsQueryOptions(params));
}

export function invalidateConversationsQueries() {
  return queryClient.invalidateQueries({
    queryKey: conversationsQueryKeys.all,
  });
}
